/**
 * Camera module for Ragsmith — "Take Picture" and "Take Video" with a
 * getUserMedia viewfinder. Falls back to <input capture> on devices without
 * getUserMedia support.
 */

import { addImageAttachment, processVideoFile } from './input.js';

// ─── State ──────────────────────────────────────────────────────────────────

let stream = null;
let facingMode = 'environment';  // rear camera default
let mode = 'photo';              // 'photo' | 'video'

// Video recording
let recorder = null;
let chunks = [];
let recordedBlob = null;
let recordedMime = '';
let recordedBytes = 0;
let recStartedAt = 0;
let recordedSeconds = 0;
let recInterval = null;   // setInterval handle (els.recTimer is the DOM node)

// Matches the server's limits (routers/chat_media.py) so a recording is
// stopped before it becomes an upload that will be rejected.
const MAX_VIDEO_SECONDS = 600;
const MAX_VIDEO_BYTES = 250 * 1024 * 1024;
// Safari only produces mp4; Chrome/Firefox only webm. Ask in preference order
// and let isTypeSupported decide.
const VIDEO_MIME_CANDIDATES = [
    'video/mp4;codecs=h264,aac',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
];

// ─── DOM References ─────────────────────────────────────────────────────────

const els = {};

function getEls() {
    if (els.modal) return els;
    els.modal = document.getElementById('camera-modal');
    els.viewfinder = document.getElementById('camera-viewfinder');
    els.video = document.getElementById('camera-video');
    els.preview = document.getElementById('camera-preview');
    els.previewImg = document.getElementById('camera-preview-img');
    els.canvas = document.getElementById('camera-canvas');
    els.shutterBtn = document.getElementById('camera-shutter');
    els.closeBtn = document.getElementById('camera-close');
    els.flipBtn = document.getElementById('camera-flip');
    els.retakeBtn = document.getElementById('camera-retake');
    els.acceptBtn = document.getElementById('camera-accept');
    els.fallbackInput = document.getElementById('camera-file-input');
    els.videoFallbackInput = document.getElementById('video-file-input');
    els.previewVideo = document.getElementById('camera-preview-video');
    els.recTimer = document.getElementById('camera-rec-timer');
    els.recElapsed = document.getElementById('camera-rec-elapsed');
    return els;
}

// ─── Camera Stream ──────────────────────────────────────────────────────────

async function startStream() {
    const { video } = getEls();
    try {
        stream = await navigator.mediaDevices.getUserMedia({
            video: {
                facingMode,
                width: { ideal: 1920 },
                height: { ideal: 1080 }
            },
            // Video mode needs the mic: the audio track is half of how the
            // model understands the clip (it gets transcribed).
            audio: mode === 'video'
        });
        video.srcObject = stream;
        await video.play();
        return true;
    } catch (err) {
        console.error('[Camera] getUserMedia failed:', err);
        return false;
    }
}

function stopStream() {
    if (stream) {
        stream.getTracks().forEach(t => t.stop());
        stream = null;
    }
    const { video } = getEls();
    if (video) video.srcObject = null;
}

// ─── Capture ────────────────────────────────────────────────────────────────

/**
 * How far the phone is turned from its natural (portrait) orientation.
 *
 * +90 is the usual landscape (turned counter-clockwise, top of the phone to
 * the left), -90 the other way, 0 for portrait or a device that cannot say.
 * The Screen Orientation API first, the legacy window.orientation second,
 * because older iOS Safari only has the latter.
 */
// The accelerometer's latest word on how the phone is physically tilted,
// sampled while the viewfinder is open. Screen orientation only says how the
// PAGE is turned, and a page under rotation lock never turns: the first
// retake recorded screen "portrait-primary" from a phone held flat sideways.
// The tilt knows regardless of any lock.
let tilt = null;

function onTilt(e) {
    if (typeof e.gamma === 'number' && typeof e.beta === 'number') {
        tilt = { beta: e.beta, gamma: e.gamma };
    }
}

function startTilt() {
    tilt = null;
    window.addEventListener('deviceorientation', onTilt);
}

function stopTilt() {
    window.removeEventListener('deviceorientation', onTilt);
}

/**
 * iOS grants motion sensors only on request, and only from a tap. The
 * camera opens from a tap, so this is asked there; a refusal simply leaves
 * the tilt unknown and the screen orientation as the only signal.
 */
async function askTiltPermission() {
    try {
        const D = window.DeviceOrientationEvent;
        if (D && typeof D.requestPermission === 'function') await D.requestPermission();
    } catch {
        /* denied or unavailable: fall back to the screen's word */
    }
}

/**
 * Landscape as the accelerometer sees it: gamma is the roll about the
 * phone's long axis, so a phone held upright in landscape has |gamma| near
 * 90 and beta near 0. Positive gamma is the right edge down — the top of the
 * phone to the left — the same turn the screen API calls landscape-primary.
 * Anything less decisive than a clear sideways hold is reported as portrait
 * rather than guessed.
 */
function tiltTurn() {
    if (!tilt) return 0;
    const { beta, gamma } = tilt;
    if (Math.abs(gamma) < 50 || Math.abs(beta) > 60) return 0;
    return gamma > 0 ? 90 : -90;
}

function screenTurn() {
    const type = screen.orientation?.type;
    if (type === 'landscape-primary') return 90;
    if (type === 'landscape-secondary') return -90;
    if (type) return 0;
    const legacy = typeof window.orientation === 'number' ? window.orientation : 0;
    return legacy === 90 || legacy === -90 ? legacy : 0;
}

// What the last shutter press knew about how the phone was held. A canvas
// export carries no EXIF, so this is the only record of it; it rides with
// the upload into the file's sidecar (see capture_facts on the server).
let lastCapture = null;

function captureFrame() {
    const { video, canvas } = getEls();
    const w = video.videoWidth;
    const h = video.videoHeight;

    // Scale down if too large (max 2048px on longest side)
    const maxDim = 2048;
    let fw = w, fh = h;
    if (w > maxDim || h > maxDim) {
        const scale = maxDim / Math.max(w, h);
        fw = Math.round(w * scale);
        fh = Math.round(h * scale);
    }

    // A phone's camera stream stays in the device's natural portrait
    // orientation however the phone is held; the browser rotates it for the
    // viewfinder and does not rotate the pixels. A canvas export carries no
    // EXIF either, so a landscape shot went out as a sideways portrait with
    // nothing anywhere to say so, and every viewer showed it standing up.
    // Turn the pixels to match the phone. Only when the frame is still
    // portrait: a browser that already delivers a landscape stream (some
    // do) needs nothing from us.
    // The screen's word first (definitive when the page really rotated),
    // the accelerometer's when the screen still claims portrait.
    const byScreen = fh > fw ? screenTurn() : 0;
    const byTilt = fh > fw && !byScreen ? tiltTurn() : 0;
    const turn = byScreen || byTilt;
    const cw = turn ? fh : fw;
    const ch = turn ? fw : fh;
    lastCapture = {
        source: 'viewfinder',
        turn,
        via: byScreen ? 'screen' : byTilt ? 'tilt' : 'none',
        screen: screen.orientation?.type || String(window.orientation ?? ''),
        tilt: tilt ? [tilt.beta, tilt.gamma] : undefined,
        frame: [w, h],
        turned: turn !== 0,
    };

    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (turn === 90) {
        // Top of the phone to the left: the scene's up points along the
        // frame's +x. Rotate a quarter turn counter-clockwise.
        ctx.translate(0, ch);
        ctx.rotate(-Math.PI / 2);
    } else if (turn === -90) {
        ctx.translate(cw, 0);
        ctx.rotate(Math.PI / 2);
    }
    ctx.drawImage(video, 0, 0, fw, fh);

    // Return as JPEG base64
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    return dataUrl;
}

// ─── Video Recording ────────────────────────────────────────────────────────

function pickVideoMime() {
    for (const m of VIDEO_MIME_CANDIDATES) {
        if (window.MediaRecorder?.isTypeSupported?.(m)) return m;
    }
    return '';  // let the browser choose
}

function extForMime(mime) {
    return (mime || '').includes('mp4') ? 'mp4' : 'webm';
}

function fmtElapsed(ms) {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function tickTimer() {
    const { recElapsed } = getEls();
    const elapsed = Date.now() - recStartedAt;
    if (recElapsed) recElapsed.textContent = fmtElapsed(elapsed);
    // Stop at the server's ceiling rather than letting the upload be rejected
    // after the user has already recorded it.
    if (elapsed >= MAX_VIDEO_SECONDS * 1000) stopRecording();
}

function startRecording() {
    const { modal, shutterBtn, recTimer: timerEl } = getEls();
    if (!stream || !window.MediaRecorder) return false;

    chunks = [];
    recordedBytes = 0;
    recordedMime = pickVideoMime();
    try {
        recorder = recordedMime
            ? new MediaRecorder(stream, { mimeType: recordedMime })
            : new MediaRecorder(stream);
    } catch (err) {
        console.error('[Camera] MediaRecorder failed:', err);
        return false;
    }
    recordedMime = recorder.mimeType || recordedMime || 'video/webm';

    recorder.ondataavailable = (e) => {
        if (!e.data?.size) return;
        chunks.push(e.data);
        recordedBytes += e.data.size;
        if (recordedBytes >= MAX_VIDEO_BYTES) stopRecording();
    };
    recorder.onstop = () => {
        recordedBlob = new Blob(chunks, { type: recordedMime });
        // MediaRecorder writes no duration into a WebM header (it was a live
        // stream), so the browser reports Infinity for it. We timed the
        // recording ourselves — use that.
        recordedSeconds = (Date.now() - recStartedAt) / 1000;
        chunks = [];
        showVideoPreview(recordedBlob);
    };
    recorder.start(1000);  // 1s chunks so the size guard can act mid-recording

    recStartedAt = Date.now();
    if (timerEl) timerEl.style.display = '';
    tickTimer();
    clearInterval(recInterval);
    recInterval = setInterval(tickTimer, 500);
    shutterBtn?.classList.add('recording');
    modal?.classList.add('recording');
    return true;
}

function stopRecording() {
    const { shutterBtn, modal, recTimer: timerEl } = getEls();
    clearInterval(recInterval);
    recInterval = null;
    if (timerEl) timerEl.style.display = 'none';
    shutterBtn?.classList.remove('recording');
    modal?.classList.remove('recording');
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    recorder = null;
}

function isRecording() {
    return !!recorder && recorder.state === 'recording';
}

// ─── Modal Flow ─────────────────────────────────────────────────────────────

function showModal() {
    startTilt();
    const { modal, viewfinder, preview } = getEls();
    modal.style.display = '';
    viewfinder.style.display = '';
    preview.style.display = 'none';
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
}

function hideModal() {
    const { modal, previewVideo } = getEls();
    if (isRecording()) stopRecording();
    modal.style.display = 'none';
    document.body.style.overflow = '';
    // Revoke the blob URL: without this every recording leaks its bytes for
    // the lifetime of the page.
    if (previewVideo?.src?.startsWith('blob:')) {
        URL.revokeObjectURL(previewVideo.src);
        previewVideo.removeAttribute('src');
        previewVideo.load();
    }
    recordedBlob = null;
    stopTilt();
    stopStream();
}

function showPreview(dataUrl) {
    const { viewfinder, preview, previewImg, previewVideo, acceptBtn } = getEls();
    viewfinder.style.display = 'none';
    preview.style.display = '';
    previewImg.style.display = '';
    previewImg.src = dataUrl;
    if (previewVideo) previewVideo.style.display = 'none';
    if (acceptBtn) acceptBtn.textContent = 'Use Photo';
}

function showVideoPreview(blob) {
    const { viewfinder, preview, previewImg, previewVideo, acceptBtn } = getEls();
    viewfinder.style.display = 'none';
    preview.style.display = '';
    if (previewImg) previewImg.style.display = 'none';
    if (previewVideo) {
        if (previewVideo.src?.startsWith('blob:')) URL.revokeObjectURL(previewVideo.src);
        previewVideo.style.display = '';
        previewVideo.src = URL.createObjectURL(blob);
    }
    if (acceptBtn) acceptBtn.textContent = 'Use Video';
}

function showViewfinder() {
    const { viewfinder, preview } = getEls();
    viewfinder.style.display = '';
    preview.style.display = 'none';
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Open the camera. Uses getUserMedia if available, falls back to <input capture>.
 */
export async function openCamera() {
    return openCapture('photo');
}

/**
 * Open the camera in video mode ("Take Video"). Records with MediaRecorder,
 * mic included, and hands the blob to the same upload path as a picked file.
 */
export async function openVideoCamera() {
    return openCapture('video');
}

async function openCapture(which) {
    getEls();
    mode = which;
    const fallback = mode === 'video' ? els.videoFallbackInput : els.fallbackInput;

    // getUserMedia needs HTTPS (or localhost); MediaRecorder is separately
    // absent on some older mobile browsers. Either way, the <input capture>
    // fallback opens the native camera app, which is a fine outcome.
    const canStream = !!navigator.mediaDevices?.getUserMedia;
    const canRecord = mode !== 'video' || !!window.MediaRecorder;
    if (!canStream || !canRecord) {
        console.log('[Camera] no getUserMedia/MediaRecorder, using fallback input');
        fallback?.click();
        return;
    }

    await askTiltPermission();  // from the tap that opened the camera
    showModal();
    const { shutterBtn } = getEls();
    if (shutterBtn) shutterBtn.title = mode === 'video' ? 'Record' : 'Take Photo';

    const ok = await startStream();
    if (!ok) {
        // Denied or failed — fall back to input capture
        hideModal();
        fallback?.click();
    }
}

/**
 * Initialize camera event listeners. Call once on app startup.
 */
export function initCamera() {
    getEls();

    // Shutter — photo: capture a frame. Video: start, then stop recording.
    els.shutterBtn?.addEventListener('click', () => {
        if (mode === 'video') {
            if (isRecording()) stopRecording();  // onstop shows the preview
            else if (!startRecording()) hideModal();
            return;
        }
        showPreview(captureFrame());
    });

    // Close — cancel and close modal
    els.closeBtn?.addEventListener('click', () => {
        hideModal();
    });

    // Flip — switch front/rear camera
    els.flipBtn?.addEventListener('click', async () => {
        facingMode = facingMode === 'environment' ? 'user' : 'environment';
        stopStream();
        const ok = await startStream();
        if (!ok) {
            // Flip failed (device may only have one camera), revert
            facingMode = facingMode === 'environment' ? 'user' : 'environment';
            await startStream();
        }
    });

    // Retake — go back to viewfinder
    els.retakeBtn?.addEventListener('click', async () => {
        showViewfinder();
        // Restart stream if it was stopped
        if (!stream) await startStream();
    });

    // Accept — feed the capture into the attachment pipeline
    els.acceptBtn?.addEventListener('click', () => {
        if (mode === 'video') {
            if (!recordedBlob) return;
            const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const file = new File(
                [recordedBlob],
                `video-${stamp}.${extForMime(recordedMime)}`,
                { type: recordedMime }
            );
            processVideoFile(file, recordedSeconds);  // same upload path as a picked file
            hideModal();
            return;
        }
        const dataUrl = els.previewImg.src;
        if (!dataUrl) return;

        // Extract base64 data (strip "data:image/jpeg;base64," prefix)
        const base64 = dataUrl.split(',')[1];
        const mediaType = 'image/jpeg';
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const name = `photo-${timestamp}.jpg`;

        addImageAttachment(base64, mediaType, name, lastCapture ? { capture: lastCapture } : null);
        hideModal();
    });

    // Escape key closes modal
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && els.modal?.style.display !== 'none') {
            hideModal();
        }
    });

    // Fallback <input capture> handler — processes file same as attach image
    els.fallbackInput?.addEventListener('change', (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Use the same processImageFile from input.js
        import('./input.js').then(({ processImageFile }) => {
            processImageFile(file);
        });

        // Reset so same file can be re-selected
        e.target.value = '';
    });

    // Video fallback — the native camera app on devices without MediaRecorder
    els.videoFallbackInput?.addEventListener('change', (e) => {
        const file = e.target.files?.[0];
        if (file) processVideoFile(file);
        e.target.value = '';
    });
}
